"use client";

import { useEffect, useState } from 'react';
import { useAppRouter as useRouter } from '@/lib/navigation';
import { useDynamicStartForm } from '@/hooks/use-dynamic-start-form';
import { StartFormFooter } from '@/components/task/start-task-footer';
import { FormSkeleton } from '@/components/skeletons/task-skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { FormProvider } from 'react-hook-form';
import {
  FormPreloadStatusProvider,
  InheritedFormModeProvider,
} from '@/forms/shared/components';
import { CamundaFormsService } from '@/lib/camunda/forms-service';
import { CamundaProcessService } from '@/lib/camunda/process-service';
import {
  getProcessDefinitionKeyFromId,
} from '@/lib/camunda/process-definition-id';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_PATH_PARAM_DECODE_ITERATIONS = 5;

interface StartFormPageProps {
  params: {
    processDefinitionId: string;
    formKey: string;
  };
}

export default function StartFormPage({ params }: StartFormPageProps) {
  const processDefinitionId = decodePathParamRepeatedly(params.processDefinitionId);
  const formKey = decodePathParamRepeatedly(params.formKey);
  const router = useRouter();
  const [latestResolution, setLatestResolution] = useState<
    | { status: 'checking'; processDefinitionId: string; formKey: string }
    | { status: 'ready'; processDefinitionId: string; formKey: string }
    | { status: 'error'; processDefinitionId: string; formKey: string; message: string }
  >({ status: 'checking', processDefinitionId, formKey });

  useEffect(() => {
    let cancelled = false;

    const resolveLatestProcessDefinition = async () => {
      const processDefinitionKey = await resolveProcessDefinitionKey(processDefinitionId);

      if (!processDefinitionKey) {
        return {
          type: 'resolution' as const,
          value: {
            status: 'error' as const,
            processDefinitionId,
            formKey,
            message: 'No se pudo resolver la clave del proceso.',
          },
        };
      }

      const latestDefinition =
        await CamundaProcessService.getLatestProcessDefinitionByKey(processDefinitionKey);

      if (!latestDefinition.id) {
        return {
          type: 'resolution' as const,
          value: {
            status: 'error' as const,
            processDefinitionId,
            formKey,
            message: 'No se pudo resolver la última versión del proceso.',
          },
        };
      }

      if (latestDefinition.id === processDefinitionId) {
        return {
          type: 'resolution' as const,
          value: {
            status: 'ready' as const,
            processDefinitionId,
            formKey,
          },
        };
      }

      const latestStartForm = await CamundaFormsService.getStartForm(latestDefinition.id);

      if (!latestStartForm?.key) {
        return {
          type: 'resolution' as const,
          value: {
            status: 'error' as const,
            processDefinitionId,
            formKey,
            message: 'No se pudo resolver el formulario de inicio de la última versión.',
          },
        };
      }

      const launchQuery = window.location.search;
      const nextPath =
        `/task-list/start-process/${encodeURIComponent(latestDefinition.id)}/${encodeURIComponent(latestStartForm.key)}` +
        (launchQuery ? launchQuery : "");
      return { type: 'redirect' as const, nextPath };
    };

    void resolveLatestProcessDefinition()
      .then((result) => {
        if (cancelled) return;
        if (result.type === 'redirect') {
          router.replace(result.nextPath);
          return;
        }
        setLatestResolution(result.value);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error resolving latest process definition:', error);
        setLatestResolution({
          status: 'error',
          processDefinitionId,
          formKey,
          message: 'No se pudo resolver la versión del proceso.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [formKey, processDefinitionId, router]);

  const isResolvingCurrentRoute =
    latestResolution.processDefinitionId === processDefinitionId &&
    latestResolution.formKey === formKey;

  if (!isResolvingCurrentRoute || latestResolution.status === 'checking') {
    return <FormSkeleton />;
  }

  if (latestResolution.status === 'error') {
    return <StartFormError message={latestResolution.message} />;
  }

  return (
    <StartFormContent
      processDefinitionId={latestResolution.processDefinitionId}
      formKey={latestResolution.formKey}
    />
  );
}

function decodePathParamRepeatedly(value: string) {
  let decoded = value;

  for (let iteration = 0; iteration < MAX_PATH_PARAM_DECODE_ITERATIONS; iteration++) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        break;
      }
      decoded = next;
    } catch {
      break;
    }
  }

  return decoded;
}

async function resolveProcessDefinitionKey(processDefinitionId: string) {
  if (processDefinitionId.includes(':')) {
    return getProcessDefinitionKeyFromId(processDefinitionId) ?? '';
  }

  if (!UUID_PATTERN.test(processDefinitionId)) {
    return processDefinitionId;
  }

  const currentDefinition = await CamundaProcessService.getProcessDefinition(processDefinitionId);
  return currentDefinition?.key || '';
}

function StartFormContent({
  processDefinitionId,
  formKey,
}: {
  processDefinitionId: string;
  formKey: string;
}) {
  const {
    FormComponent,
    SuccessComponent,
    submissionState,
    submittedData,
    processInstanceId,
    loading,
    error,
    form,
    handleSubmit,
    isSubmitting,
    processDefinitionName,
    preloadedData,
    preloadPending,
  } = useDynamicStartForm(processDefinitionId, formKey);

  if (loading) {
    return <FormSkeleton />;
  }

  if (error) {
    return <StartFormError message={error} />;
  }

  if (submissionState === 'success' && SuccessComponent) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-6">
        <SuccessComponent 
          submittedData={submittedData || {}} 
          processInstanceId={processInstanceId}
        />
      </div>
    );
  }

  if (!FormComponent) {
    return (
      <Card className="m-6">
        <CardContent className="p-6">
          <div className="text-center">
            <p>No se pudo cargar el formulario</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <InheritedFormModeProvider>
        <div className="h-full flex flex-col">
          <div className="flex-1 overflow-auto">
            <div className="p-6">
              <div className="mb-4">
                <h2 className="text-lg font-semibold">
                  {processDefinitionName}
                </h2>
              </div>

              <FormPreloadStatusProvider pending={preloadPending}>
                <FormComponent
                  processDefinitionId={processDefinitionId}
                  variables={{}}
                  preloadedData={preloadedData || undefined}
                />
              </FormPreloadStatusProvider>
            </div>
          </div>

          { }
          <StartFormFooter
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            processDefinitionId={processDefinitionId}
          />
        </div>
      </InheritedFormModeProvider>
    </FormProvider>
  );
}

function StartFormError({ message }: { message: string }) {
  return (
    <Card className="m-6">
      <CardContent className="p-6">
        <div className="text-center text-red-600">
          <p className="font-semibold">Error</p>
          <p>{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
